import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static values = {
        shareText: String,
        shareUrl: String,
        androidAppUrl: String,
        installAppLabel: String,
        iosAppUrl: String,
        installAppIosLabel: String,
        successMessage: String,
        errorMessage: String,
        shareOfficialVideoLines: Array,
        shareOfficialVideosIntro: String
    }

    async share(event) {
        event.preventDefault()

        const url = this.shareUrlValue
        const text = this.shareTextValue || ""
        const fullText = this.buildFullShareText(text, url)

        // 1️⃣ Web Share API (best case)
        // Do not pass `url` separately: many apps (e.g. WhatsApp) append it again after `text`,
        // duplicating the referral link already inside fullText.
        if (navigator.share) {
            try {
                await navigator.share({title: "Inntro", text: fullText})
                return
            } catch (err) {
                // Android WebView often fails silently → continue
            }
        }

        // 2️⃣ Clipboard (may fail silently on Android 12)
        const copied = await this.tryClipboard(fullText)
        if (copied) {
            this.showSuccess(this.successMessageValue)
            return
        }

        // 3️⃣ ANDROID INTENT (ultimate fallback)
        if (this.isAndroid()) {
            this.openAndroidIntent(fullText)
            return
        }

        // 4️⃣ Last resort
        this.showError(this.errorMessageValue)
    }

    buildFullShareText(text, url) {
        let full = [text.trim(), url].filter(Boolean).join(" ")
        if (this.hasAndroidAppUrlValue && this.androidAppUrlValue) {
            const label = this.hasInstallAppLabelValue ? this.installAppLabelValue : "Download the app (Android):"
            full += `\n\n${label} ${this.androidAppUrlValue}`
        }
        if (this.hasIosAppUrlValue && this.iosAppUrlValue) {
            const label = this.hasInstallAppIosLabelValue ? this.installAppIosLabelValue : "Download the app (App Store):"
            full += `\n\n${label} ${this.iosAppUrlValue}`
        }
        const videoLines = this.hasShareOfficialVideoLinesValue ? this.shareOfficialVideoLinesValue : []
        if (Array.isArray(videoLines) && videoLines.length > 0) {
            const intro = (this.hasShareOfficialVideosIntroValue ? this.shareOfficialVideosIntroValue : "").trim()
            const body = videoLines.map((row) => `${row.label}: ${row.url}`).join("\n")
            full += intro ? `\n\n${intro}\n${body}` : `\n\n${body}`
        }
        return full
    }

    async tryClipboard(text) {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text)
                return true
            }
        } catch (_) {}

        try {
            const textarea = document.createElement("textarea")
            textarea.value = text
            textarea.setAttribute("readonly", "")
            textarea.style.position = "fixed"
            textarea.style.opacity = "0"
            document.body.appendChild(textarea)
            textarea.select()
            const success = document.execCommand("copy")
            document.body.removeChild(textarea)
            return success
        } catch (_) {
            return false
        }
    }

    openAndroidIntent(fullText) {
        const intentUrl =
            "intent://send/#Intent;" +
            "action=android.intent.action.SEND;" +
            "type=text/plain;" +
            `S.android.intent.extra.TEXT=${encodeURIComponent(fullText)};end;`

        window.location.href = intentUrl
    }

    isAndroid() {
        return /Android/i.test(navigator.userAgent)
    }

    showSuccess(message) {
        if (!message) return
        if (window.Turbo) {
            this.dispatch("success", { detail: { message } })
        } else {
            alert(message)
        }
    }

    showError(message) {
        if (!message) return
        if (window.Turbo) {
            this.dispatch("error", { detail: { message } })
        } else {
            alert(message)
        }
    }
}
